import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, PlusIcon } from 'lucide-react';
import { useWorkspace } from '../../contexts/AuthContext';
import { emptyQuestion, saveForm } from '../../services/formService';
import { saveLanding } from '../../services/landingService';
import { uid } from '../../utils/format';
import { Form, FormQuestion, PostSubmitConfig } from '../../types';
import { DEFAULT_POST_SUBMIT, isValidUrl } from '../../utils/postSubmit';
import { PageHeader } from '../../components/crm/PageHeader';
import { QuestionCard } from '../../components/forms/QuestionCard';
import { DevicePreview, Device } from '../../components/shared/DevicePreview';
import { DynamicForm } from '../../components/public/DynamicForm';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Switch } from '../../components/ui/Switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import { formUrl } from '../../utils/clipboard';
import { normalizeSlug } from '../../config/app';
export function FormEditor() {
  const {
    id = ''
  } = useParams();
  const {
    forms,
    landings
  } = useWorkspace();
  const stored = forms.find(f => f.id === id);
  const [draft, setDraft] = useState<Form | null>(stored ?? null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  useEffect(() => {
    if (stored && (!draft || draft.id !== stored.id)) setDraft(stored);
  }, [stored, draft]);
  const sortedQuestions = useMemo(() => draft ? [...draft.questions].sort((a, b) => a.order - b.order) : [], [draft]);
  if (!draft) {
    return <div className="rounded-lg border border-border bg-background p-10 text-center" data-id="element-986" data-mp="1yq22ic80krna">
        <p className="text-sm text-muted-foreground" data-id="element-987" data-mp="1ysejon5y6xza">Este formulario no existe.</p>
        <Link to="/app/formularios" className="mt-4 inline-block" data-id="element-988" data-mp="23oby2nxnhu9f">
          <Button variant="secondary" data-id="element-989" data-mp="1oz7jdgq2e862">Volver a formularios</Button>
        </Link>
      </div>;
  }
  function update(partial: Partial<Form>) {
    setDraft(prev => prev ? {
      ...prev,
      ...partial
    } : prev);
    setSaved(false);
  }
  const privacyNotice = draft.privacyNotice ?? {
    enabled: true,
    text: 'Al enviar este formulario aceptas que utilicemos tus datos para responder a tu solicitud.',
    linkLabel: 'Consulta nuestra política de privacidad.'
  };
  /** Acción posterior al envío: página de gracias, WhatsApp, URL o mensaje. */
  const postSubmit = {
    ...DEFAULT_POST_SUBMIT,
    ...(draft.postSubmit ?? {})
  };
  function updatePostSubmit(partial: Partial<PostSubmitConfig>) {
    update({
      postSubmit: {
        ...postSubmit,
        ...partial
      }
    });
  }
  function updateQuestion(question: FormQuestion) {
    update({
      questions: draft!.questions.map(q => q.id === question.id ? question : q)
    });
  }
  function addQuestion() {
    update({
      questions: [...draft!.questions, emptyQuestion(draft!.id, draft!.questions.length + 1)]
    });
  }
  function duplicateQuestion(question: FormQuestion) {
    const copy: FormQuestion = {
      ...JSON.parse(JSON.stringify(question)),
      id: uid('q'),
      label: `${question.label} (copia)`,
      order: draft!.questions.length + 1
    };
    update({
      questions: [...draft!.questions, copy]
    });
  }
  function reorder(targetId: string) {
    if (!dragId || dragId === targetId) return;
    const ids = sortedQuestions.map(q => q.id);
    const fromIndex = ids.indexOf(dragId);
    const toIndex = ids.indexOf(targetId);
    ids.splice(toIndex, 0, ids.splice(fromIndex, 1)[0]);
    update({
      questions: draft!.questions.map(q => ({
        ...q,
        order: ids.indexOf(q.id) + 1
      }))
    });
    setDragId(null);
  }
  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      await saveForm(draft!);
      setSaved(true);
    } catch (err) {
      // Se muestra la causa real y el editor no se cierra ni pierde cambios.
      setSaveError(err instanceof Error ? err.message : 'Error desconocido.');
    } finally {
      setSaving(false);
    }
  }
  async function associateLanding(landingId: string) {
    const landing = landings.find(l => l.id === landingId);
    if (!landing) return;
    await saveLanding({
      ...landing,
      formId: draft!.id
    });
  }
  const linkedLanding = landings.find(l => l.formId === draft.id);
  return <>
      <Link to="/app/formularios" className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-id="element-990" data-mp="25dmd5wc35zwp">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-991" data-mp="1cvquobp3tive" />
        Volver a formularios
      </Link>

      <PageHeader title={draft.name} description={`${draft.questions.length} preguntas · ${draft.status === 'activo' ? 'Activo' : draft.status === 'borrador' ? 'Borrador' : 'Inactivo'}`} actions={<>
            {saving ? <span className="self-center text-sm text-muted-foreground" data-id="element-993" data-mp="wb1jgz3umqdq">
                Guardando…
              </span> : saved ? <span className="flex items-center gap-1.5 self-center text-sm text-green-600" data-id="element-994" data-mp="rbexo19yg1tb">
                <CheckIcon className="h-4 w-4" data-id="element-995" data-mp="mef1ci2wqdfe" /> Guardado correctamente
              </span> : <span className="self-center text-sm text-muted-foreground" data-id="element-996" data-mp="hm54ei3dkd3m">
                Cambios sin guardar
              </span>}
            <Button variant="secondary" onClick={() => window.open(`/formulario/${draft.slug}`, '_blank')} data-id="element-997" data-mp="codeftmaj122">
              Vista previa pública
            </Button>
            <Button onClick={handleSave} loading={saving} data-id="element-998" data-mp="3mm5mfwgee3z">
              Guardar cambios
            </Button>
          </>} data-id="element-992" data-mp="15bzfau44vrl8" />

      {saveError && <div role="alert" className="mb-5 flex flex-col gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 sm:flex-row sm:items-center sm:justify-between" data-id="element-999" data-mp="3uuhwjocu29mu">
          <span data-id="element-1000" data-mp="3q5p7sd7lwpe1">
            <strong className="font-semibold" data-id="element-1001" data-mp="3q68su2sjztwa">Error al guardar. </strong>
            {saveError}
          </span>
          <Button variant="secondary" onClick={handleSave} loading={saving} data-id="element-1002" data-mp="3b97xlpb5lgt2">
            Reintentar
          </Button>
        </div>}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,520px)]" data-id="element-1003" data-mp="7qy7f8qso8kb">
        <div className="min-w-0" data-id="element-1004" data-mp="7t0vvocattmx">
          <Tabs defaultValue="preguntas" data-id="element-1005" data-mp="cqaw7z187ppj">
            <TabsList data-id="element-1006" data-mp="cozoxp8knzto">
              <TabsTrigger value="preguntas" data-id="element-1007" data-mp="crlw3nrjwdbd">Preguntas</TabsTrigger>
              <TabsTrigger value="ajustes" data-id="element-1008" data-mp="3u5pwqrsc0izc">Ajustes</TabsTrigger>
            </TabsList>

            <TabsContent value="preguntas" data-id="element-1009" data-mp="2yn6c2kbigq6">
              <ul className="space-y-3" data-id="element-1010" data-mp="qpiverxe2xtq">
                {sortedQuestions.map((question, index) => <QuestionCard key={question.id} question={question} index={index} previousQuestions={sortedQuestions.slice(0, index)} dragging={dragId === question.id} onChange={updateQuestion} onDuplicate={() => duplicateQuestion(question)} onDelete={() => update({
                questions: draft.questions.filter(q => q.id !== question.id)
              })} onDragStart={() => setDragId(question.id)} onDragOver={e => e.preventDefault()} onDrop={() => reorder(question.id)} data-id="element-1011" data-mp="qnfx7km31ydx" data-mpi={index} />)}
              </ul>
              {sortedQuestions.length === 0 && <p className="rounded-lg border border-dashed border-border bg-surface p-8 text-center text-sm text-muted-foreground" data-id="element-1012" data-mp="1x7k35jtyyn7x">
                  Este formulario todavía no tiene preguntas.
                </p>}
              <Button className="mt-4" variant="secondary" leftIcon={<PlusIcon className="h-4 w-4" data-id="element-1014" data-mp="2665eov10x1z7" />} onClick={addQuestion} data-id="element-1013" data-mp="268qhbm95bly0">
                Añadir pregunta
              </Button>
            </TabsContent>

            <TabsContent value="ajustes" data-id="element-1015" data-mp="3hmlzz4z9ectz">
              <div className="space-y-5 rounded-lg border border-border bg-background p-5" data-id="element-1016" data-mp="3hpq7ab80toov">
                <Input id="form-name" label="Nombre del formulario" value={draft.name} onChange={e => update({
                name: e.target.value
              })} data-id="element-1017" data-mp="3do4qiesd8q9e" />
                <Textarea id="form-desc" label="Descripción" rows={3} value={draft.description ?? ''} onChange={e => update({
                description: e.target.value
              })} data-id="element-1018" data-mp="1fz9btuvcxx29" />
                <Input id="form-slug" label="Enlace público" helperText={formUrl(draft.slug)} value={draft.slug} onChange={e => update({
                slug: normalizeSlug(e.target.value)
              })} data-id="element-1019" data-mp="1ppcdejgjojc7" />
                <Select id="form-status" label="Estado" options={[{
                value: 'borrador',
                label: 'Borrador'
              }, {
                value: 'activo',
                label: 'Activo'
              }, {
                value: 'inactivo',
                label: 'Inactivo'
              }]} value={draft.status} onChange={e => update({
                status: e.target.value as Form['status']
              })} data-id="element-1020" data-mp="1yno0ibi7cyzw" />
                <Input id="form-cta" label="Texto del botón de envío" value={draft.submitLabel} onChange={e => update({
                submitLabel: e.target.value
              })} data-id="element-1021" data-mp="2rjdphd1jj2ur" />
                <Textarea id="form-success" label="Mensaje de éxito" rows={2} value={draft.successMessage} onChange={e => update({
                successMessage: e.target.value
              })} data-id="element-1022" data-mp="2mqkmoupmu4lb" />

                {/* Acción posterior: el lead SIEMPRE se guarda antes. */}
                <div className="space-y-4 rounded-xl border border-border bg-surface p-4" data-id="element-1023" data-mp="3ahq1d6ju6qu1">
                  <div data-id="element-1024" data-mp="36hzcx9hs8mso">
                    <h3 className="text-sm font-semibold text-foreground" data-id="element-1025" data-mp="36ejxu6o3zdr9">
                      Acción después del formulario
                    </h3>
                    <p className="mt-1 text-xs text-muted-foreground" data-id="element-1026" data-mp="2wj9thca8muab">
                      Qué ve el visitante al terminar. El contacto se guarda en
                      tu CRM antes de ejecutar la acción.
                    </p>
                  </div>
                  <Select id="form-post-action" label="Acción principal" options={[{
                  value: 'gracias',
                  label: 'Mostrar página de gracias'
                }, {
                  value: 'whatsapp',
                  label: 'Abrir conversación de WhatsApp'
                }, {
                  value: 'url',
                  label: 'Redirigir a una URL externa'
                }, {
                  value: 'mensaje',
                  label: 'Mostrar mensaje simple de confirmación'
                }]} value={postSubmit.action} onChange={e => updatePostSubmit({
                  action: e.target.value as PostSubmitConfig['action']
                })} data-id="element-1027" data-mp="1l5o7fzzod8t0" />

                  {postSubmit.action === 'gracias' && <Select id="form-gracias" label="Página de agradecimiento" placeholder="Selecciona una landing" options={landings.map(l => ({
                  value: l.slug,
                  label: l.name
                }))} value={draft.thankYouSlug ?? ''} onChange={e => update({
                  thankYouSlug: e.target.value
                })} data-id="element-1028" data-mp="epze6x6qjb3l" />}

                  {postSubmit.action === 'whatsapp' && <>
                      <Input id="form-wa-number" label="Número de WhatsApp con prefijo" placeholder="+34 600 123 456" value={postSubmit.whatsappNumber ?? ''} onChange={e => updatePostSubmit({
                    whatsappNumber: e.target.value
                  })} data-id="element-1029" data-mp="10xwg0ntsj3ed" />
                      <Textarea id="form-wa-message" label="Mensaje predeterminado" rows={3} value={postSubmit.whatsappMessage ?? ''} onChange={e => updatePostSubmit({
                    whatsappMessage: e.target.value
                  })} data-id="element-1030" data-mp="d9lkm06v5vxh" />
                      <p className="text-xs text-muted-foreground" data-id="element-1031" data-mp="3ch4oi3m2wp4">
                        Variables disponibles: {'{{full_name}}'},{' '}
                        {'{{company}}'}, {'{{phone}}'}, {'{{email}}'} y el
                        nombre de cualquier pregunta, por ejemplo{' '}
                        {'{{presupuesto_disponible}}'}.
                      </p>
                      <Input id="form-wa-label" label="Texto del botón" placeholder="Hablar por WhatsApp" value={postSubmit.whatsappLabel ?? ''} onChange={e => updatePostSubmit({
                    whatsappLabel: e.target.value
                  })} data-id="element-1032" data-mp="217nq5bfjsrdo" />
                      <Switch id="form-wa-auto" label="Abrir WhatsApp automáticamente al enviar" checked={postSubmit.whatsappAuto === true} onChange={checked => updatePostSubmit({
                    whatsappAuto: checked
                  })} data-id="element-1033" data-mp="1r96sjvm3tein" />
                    </>}

                  {postSubmit.action === 'url' && <>
                      <Input id="form-post-url" label="URL de destino" placeholder="https://calendly.com/tuempresa/reunion" value={postSubmit.redirectUrl ?? ''} onChange={e => updatePostSubmit({
                    redirectUrl: e.target.value
                  })} data-id="element-1034" data-mp="2a7sv7ex9rdoo" />
                      {postSubmit.redirectUrl && !isValidUrl(postSubmit.redirectUrl) && <p role="alert" className="text-xs text-red-600" data-id="element-1035" data-mp="2xx56ckc5pvtm">
                            Introduce una URL válida que empiece por https://
                          </p>}
                    </>}

                  {postSubmit.action === 'mensaje' && <>
                      <Input id="form-post-title" label="Título" value={postSubmit.messageTitle ?? ''} onChange={e => updatePostSubmit({
                    messageTitle: e.target.value
                  })} data-id="element-1036" data-mp="3f6pivxwivzoi" />
                      <Textarea id="form-post-text" label="Texto" rows={2} value={postSubmit.messageText ?? ''} onChange={e => updatePostSubmit({
                    messageText: e.target.value
                  })} data-id="element-1037" data-mp="3p557nu82uwuu" />
                    </>}
                </div>

                <Switch id="form-save-progress" label="Guardar progreso del visitante" checked={draft.saveProgress === true} onChange={checked => update({
                saveProgress: checked
              })} data-id="element-1038" data-mp="1sl0bui51hzs" />
                <p className="-mt-2 text-xs text-muted-foreground" data-id="element-1039" data-mp="bo5y1gg6bw3q">
                  Desactivado, cada visita empieza siempre en el primer paso.
                </p>

                <Input id="form-redirect" label="URL de redirección (opcional)" placeholder="https://…" value={draft.redirectUrl ?? ''} onChange={e => update({
                redirectUrl: e.target.value
              })} data-id="element-1040" data-mp="1df5spqmpc5ad" />
                {/* Aviso informativo: no obliga a marcar nada para enviar. */}
                <div className="space-y-4 rounded-xl border border-border bg-surface p-4" data-id="element-1041" data-mp="1i7p72pt7r4g8">
                  <Switch id="form-privacy-enabled" label="Mostrar aviso de privacidad" checked={privacyNotice.enabled} onChange={checked => update({
                  privacyNotice: {
                    ...privacyNotice,
                    enabled: checked
                  }
                })} data-id="element-1042" data-mp="uie4r3w7t1zz" />
                  <p className="text-xs text-muted-foreground" data-id="element-1043" data-mp="zf4ihkkjrli8">
                    Texto informativo al pie del formulario. No obliga a marcar
                    nada para poder enviarlo.
                  </p>
                  {privacyNotice.enabled && <>
                      <Textarea id="form-privacy-text" label="Texto del aviso" rows={2} value={privacyNotice.text} onChange={e => update({
                    privacyNotice: {
                      ...privacyNotice,
                      text: e.target.value
                    }
                  })} data-id="element-1044" data-mp="2ieuath3tgiyw" />
                      <Input id="form-privacy-link-label" label="Texto del enlace (opcional)" placeholder="Consulta nuestra política de privacidad." value={privacyNotice.linkLabel ?? ''} onChange={e => update({
                    privacyNotice: {
                      ...privacyNotice,
                      linkLabel: e.target.value
                    }
                  })} data-id="element-1045" data-mp="2dfhe84n57xj4" />
                      <Input id="form-privacy-link-url" label="Enlace a tu política de privacidad (opcional)" placeholder="https://tuempresa.com/privacidad" value={privacyNotice.linkUrl ?? ''} onChange={e => update({
                    privacyNotice: {
                      ...privacyNotice,
                      linkUrl: e.target.value
                    }
                  })} data-id="element-1046" data-mp="2wevcgyrwnuff" />
                    </>}
                </div>
                <Select id="form-landing" label="Asociar a una landing" placeholder={linkedLanding ? linkedLanding.name : 'Selecciona una landing'} options={landings.map(l => ({
                value: l.id,
                label: l.name
              }))} value={linkedLanding?.id ?? ''} onChange={e => associateLanding(e.target.value)} data-id="element-1047" data-mp="3ttegwneewomg" />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <FormPreviewPanel form={draft} data-id="element-1048" data-mp="3b127jgpccn2t" />
      </div>
    </>;
}
function FormPreviewPanel({
  form
}: {
  form: Form;
}) {
  const [device, setDevice] = useState<Device>('desktop');
  return <div className="h-[720px] xl:sticky xl:top-24" data-id="element-1049" data-mp="3226zdz2ql2wv">
      <DevicePreview device={device} onDeviceChange={setDevice} data-id="element-1050" data-mp="31zcfq9rjrm4i">
        <div className="bg-surface p-4" data-id="element-1051" data-mp="2x1japk1zcy5t">
          <DynamicForm key={form.questions.length} form={form} preview data-id="element-1052" data-mp="2s2zl99o6h48p" />
        </div>
      </DevicePreview>
    </div>;
}